/* predictive-search.js: <sf-predictive-search>, the search sheet's live suggestions (chrome.md §2.5).
   A debounced fetch of {{ routes.predictive_search_url }}?q=…&section_id=predictive-search is swapped into the
   results host. Arrow keys, Enter and Escape are handled here.

   Recent searches live on this device only (theme.storage 'recent-searches', which is localStorage 'sf:recent-searches').
   The list keeps the last 5, newest first, de-duplicated case-insensitively. Every read and write is wrapped in
   try/catch, so a blocked or full localStorage simply means no recents. A term is saved when a search is
   submitted and when a suggestion, result or brand chip is chosen (never for a Browse link, which is navigation,
   not a search). Tapping a recent term runs that search again,
   ✕ removes one, and "Clear all" empties the list. With no recents the group is hidden, so there is never a
   heading with nothing under it.

   Three resource tabs, Products (default), Collections and Pages, map onto the API's resources[type]. The query
   is kept when switching. Each request carries a sequence number, so a slow earlier response can never overwrite
   a newer one. The full-results action follows the tab AND the platform: /search?type= supports product and
   page, but there is no collection results page, so Collections offers the collection index instead.

   In the sheet (sf-drawer#search-sheet) on a phone:
   · focus. The input is the drawer's [data-drawer-initial], so sf-drawer.open() focuses it synchronously inside
     the opener's tap. The drawer:open handler below re-checks that, also synchronously. Nothing waits for a timer
     or an animation, because a focus outside the gesture leaves iOS without a keyboard.
   · keyboard. The sheet is sized to window.visualViewport (height + offsetTop, kept current on its resize and
     scroll), so its bottom edge sits on top of the keyboard instead of behind it. While the keyboard is up the
     sheet goes compact (.is-kb): tighter rows, one-line sideways-scrolling chip rows, and no big Search button.
     The keyboard's own Search key (enterkeyhint="search") submits.
   · closing blurs the input; sf-drawer returns focus to whichever trigger opened it. Leaving the page (pagehide)
     and coming back from the back/forward cache (pageshow.persisted) settle the sheet closed and cleared, so the
     next tap opens it fresh.
   Before typing, the products panel shows recent searches, the "Popular brands" chips (<sf-search-brands>, from
   search-brands.js: only brands proven in stock, filled in after the sheet opens) and the Browse row
   (snippets/search-brands.liquid / search-browse.liquid). Arrow keys walk all of them with real focus.
   Progressive: the <form> still submits to /search without JS. Requires theme.js.
   Product suggestions pass the same relevance gate as the results page (sections/predictive-search.liquid ·
   snippets/search-relevance.liquid). The products tab asks the API for a few more than it shows, so a dropped
   product does not leave the list short; _trim cuts the answer back to data-limit. */
'use strict';
(function () {
  /* An Arabic brand or model name typed as the whole query is searched as its Latin name. Shopify has no Arabic
     text for these products, so its own matching found only a fraction of them (نايك gave 11 of the 29 Nike
     products); the relevance gate (snippets/search-relevance.liquid) can only remove results, never add them.
     Exact whole-query matches only, folded the way the gate folds Arabic. */
  const AR_ALIAS = {
    'نايك': 'nike', 'نايكي': 'nike', 'اديداس': 'adidas', 'اديدس': 'adidas',
    'نيو بالانس': 'new balance', 'نيو بالنس': 'new balance', 'نيوبالانس': 'new balance', 'نيوبالنس': 'new balance',
    'اسيكس': 'asics', 'اسكس': 'asics', 'سوكوني': 'saucony', 'ساكوني': 'saucony',
    'كونفرس': 'converse', 'كونفيرس': 'converse', 'كونفرز': 'converse', 'كونفيرز': 'converse', 'اون': 'on',
    'ايت ماي سوكس': 'eat my socks', 'سامبا': 'samba', 'جازيل': 'gazelle', 'كامبوس': 'campus', 'سوبرستار': 'superstar',
    'دانك': 'dunk', 'جوردان': 'jordan', 'اير فورس': 'air force', 'اير ماكس': 'air max', 'ستان سميث': 'stan smith',
  };
  const foldAr = (q) => String(q || '').trim().replace(/[أإآٱ]/g, 'ا').replace(/ة/g, 'ه').replace(/ى/g, 'ي').replace(/[ـً-ْ]/g, '').replace(/\s+/g, ' ');
  const latinFor = (q) => AR_ALIAS[foldAr(q)] || null;
  /* a results page opened straight on an Arabic alias (a shared link, a recent search) is moved to the Latin query */
  try {
    if (/\/search\/?$/.test(location.pathname)) {
      const u = new URL(location.href);
      const latin = latinFor(u.searchParams.get('q'));
      if (latin && !u.searchParams.get('section_id')) { u.searchParams.set('q', latin); location.replace(u.toString()); }
    }
  } catch (e) { /* no rewrite: the gate still shows only true matches */ }
  const theme = window.theme || (window.theme = {});
  const RECENT_KEY = 'recent-searches';
  const RECENT_MAX = 5;
  const MOBILE = '(max-width: 767.98px)';
  /* resources[type] per tab. 'query' (did-you-mean terms) only makes sense alongside products. */
  const TABS = {
    product: { types: 'product,query', searchType: 'product' },
    collection: { types: 'collection', searchType: null },
    page: { types: 'page', searchType: 'page' }
  };
  const CLOCK = '<span class="sresult__chip sresult__chip--plain" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8.5"/><path d="M12 8v4l3 2"/></svg></span>';
  const CROSS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M7 7l10 10M17 7L7 17"/></svg>';
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const norm = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim().slice(0, 80);
  const fold = (s) => norm(s).toLowerCase();

  /* ---- recent searches (device only) ---------------------------------------------------------------- */
  const rawKey = 'sf:' + RECENT_KEY;
  function readRaw() {
    try {
      const v = theme.storage && theme.storage.get ? theme.storage.get(RECENT_KEY, []) : JSON.parse(window.localStorage.getItem(rawKey) || '[]');
      return Array.isArray(v) ? v : [];
    } catch (e) { return []; }
  }
  function writeRaw(list) {
    try {
      if (!list.length) {
        if (theme.storage && theme.storage.remove) theme.storage.remove(RECENT_KEY); else window.localStorage.removeItem(rawKey);
      } else if (theme.storage && theme.storage.set) {
        theme.storage.set(RECENT_KEY, list);
      } else {
        window.localStorage.setItem(rawKey, JSON.stringify(list));
      }
    } catch (e) { /* storage blocked or full: recents are a convenience, never an error */ }
  }
  const Recent = {
    list() {
      const seen = new Set();
      const out = [];
      readRaw().forEach((s) => {
        if (typeof s !== 'string') return;
        const q = norm(s);
        const k = q.toLowerCase();
        if (!q || seen.has(k)) return;
        seen.add(k);
        out.push(q);
      });
      return out.slice(0, RECENT_MAX);
    },
    add(q) {
      q = norm(q);
      if (!q) return;
      const k = q.toLowerCase();
      writeRaw([q].concat(Recent.list().filter((r) => r.toLowerCase() !== k)).slice(0, RECENT_MAX));
    },
    remove(q) {
      const k = fold(q);
      writeRaw(Recent.list().filter((r) => r.toLowerCase() !== k));
    },
    clear() { writeRaw([]); }
  };
  theme.recentSearches = Recent;

  const visible = (el) => !!el && !el.closest('[hidden]') && el.getClientRects().length > 0;
  const focusEl = (el) => { if (!el) return; try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } };
  let uid = 0;

  class SFPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._uid = 'sps' + (++uid);
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-predictive-input]');
      this.panel = this.querySelector('[data-search-panel]');
      this.host = this.querySelector('[data-predictive-host]');
      this.defaults = this.querySelector('[data-search-default]');
      this.recentHost = this.querySelector('[data-search-recent]');
      this.status = this.querySelector('[data-search-status]');
      if (!this.input || !this.host) return;
      if (!this.host.id) this.host.id = this._uid + '-results';
      this.recentOn = this.dataset.recent !== 'false';
      this.tabs = Array.from(this.querySelectorAll('[data-search-tab]'));
      this.typeField = this.querySelector('[data-search-type]');
      this.submit = this.querySelector('[data-search-submit]');
      this.tab = 'product';
      this.seq = 0;
      this.url = this.dataset.url || '/search/suggest';
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.limit = parseInt(this.dataset.limit, 10) || 6;
      this.active = -1;
      this._ctrlAll = new AbortController();
      const signal = this._ctrlAll.signal;

      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', this.host.id);
      if (this.input.type !== 'search') this.input.type = 'search';
      if (!this.input.hasAttribute('enterkeyhint')) this.input.setAttribute('enterkeyhint', 'search');
      this.input.readOnly = false;

      this.tabs.forEach((btn) => {
        btn.addEventListener('click', () => this.selectTab(btn.dataset.searchTab), { signal });
        btn.addEventListener('keydown', (e) => {
          const i = this.tabs.indexOf(btn);
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            /* in RTL the visual order is mirrored, so ArrowRight moves to the previous tab */
            const rtl = getComputedStyle(this).direction === 'rtl';
            const step = (e.key === 'ArrowRight') !== rtl ? 1 : -1;
            const next = this.tabs[(i + step + this.tabs.length) % this.tabs.length];
            next.focus();
            this.selectTab(next.dataset.searchTab);
          }
        }, { signal });
      });
      this.input.addEventListener('input', () => this.onInput(), { signal });
      this.input.addEventListener('keydown', (e) => this.onKey(e), { signal });
      this.input.addEventListener('focus', () => {
        if (!this.input.value.trim() && !this.host.hidden) this.showDefaults();
      }, { signal });
      if (this.panel) this.panel.addEventListener('keydown', (e) => this.onPanelKey(e), { signal });
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e), { signal });
      this.addEventListener('click', (e) => this.onClick(e), { signal });
      /* the brand chips arrive after the sheet opens; recheck whether the panel has anything to show */
      this.addEventListener('search-brands:change', () => this.updateEmpty(), { signal });

      this.sheet = this.closest('sf-drawer');
      if (this.sheet) this.setupSheet(this.sheet, signal);
      this.renderRecent();
      this.updateEmpty();
    }

    disconnectedCallback() {
      if (this._ctrlAll) this._ctrlAll.abort();
      if (this._ctrl) this._ctrl.abort();
      this.stopViewport();
      clearTimeout(this._timer);
      clearTimeout(this._skelTimer);
    }

    remember(q) { if (this.recentOn) Recent.add(q); }

    focusInput() { focusEl(this.input); }

    /* ---- the sheet: focus, visual viewport, compact keyboard mode, bfcache ----------------------------- */
    setupSheet(sheet, signal) {
      sheet.addEventListener('drawer:open', (e) => {
        if (e.target !== sheet) return;
        /* still inside the opener's tap/click: a focus here raises the on-screen keyboard */
        if (document.activeElement !== this.input) this.focusInput();
        this.renderRecent();
        if (!this.input.value.trim()) this.showDefaults();
        this.startViewport();
      }, { signal });
      sheet.addEventListener('drawer:close', (e) => {
        if (e.target !== sheet) return;
        if (document.activeElement === this.input) this.input.blur();
        this.classList.remove('is-typing');
        sheet.classList.remove('is-kb');
        clearTimeout(this._stopTimer);
        /* keep the viewport sizing through the exit motion so the panel does not jump while it leaves */
        this._stopTimer = setTimeout(() => { if (!sheet.isOpen) this.stopViewport(); }, 700);
      }, { signal });
      const recheck = () => { if (this._vv) this.updateViewport(); };
      this.input.addEventListener('focus', recheck, { signal });
      this.input.addEventListener('blur', recheck, { signal });
      /* Leaving with the sheet open, then coming back through the back/forward cache, used to restore a sheet
         that looked open but had no keyboard and a stale query. Settle it closed and cleared instead. */
      window.addEventListener('pagehide', () => this.settleSheet(), { signal });
      window.addEventListener('pageshow', (e) => { if (e.persisted) this.settleSheet(); }, { signal });
    }

    settleSheet() {
      const sheet = this.sheet;
      if (!sheet) return;
      if (sheet.isOpen || !sheet.hidden) {
        if (typeof sheet.close === 'function') sheet.close();
        sheet.hidden = true;
        sheet.classList.remove('is-open', 'is-closing');
      }
      if (document.activeElement === this.input) this.input.blur();
      if (this.input.value) this.input.value = '';
      this.classList.remove('is-typing');
      this.showDefaults();
      clearTimeout(this._stopTimer);
      this.stopViewport();
    }

    startViewport() {
      clearTimeout(this._stopTimer);
      if (this._vv) { this.updateViewport(); return; }
      this._vv = new AbortController();
      this._vvMax = 0;
      const signal = this._vv.signal;
      const vv = window.visualViewport;
      const update = () => this.updateViewport();
      if (vv) {
        vv.addEventListener('resize', update, { signal });
        vv.addEventListener('scroll', update, { signal });
      }
      window.addEventListener('resize', update, { signal });
      window.addEventListener('orientationchange', update, { signal });
      this.updateViewport();
    }

    stopViewport() {
      if (this._vv) { this._vv.abort(); this._vv = null; }
      this._vvMax = 0;
      const sheet = this.sheet;
      if (!sheet) return;
      sheet.classList.remove('is-vv', 'is-kb');
      sheet.style.removeProperty('--sheet-vv-h');
      sheet.style.removeProperty('--sheet-vv-top');
    }

    updateViewport() {
      const sheet = this.sheet;
      const vv = window.visualViewport;
      if (!sheet) return;
      const mobile = window.matchMedia ? window.matchMedia(MOBILE).matches : window.innerWidth < 768;
      if (!vv || !mobile) {
        sheet.classList.remove('is-vv', 'is-kb');
        sheet.style.removeProperty('--sheet-vv-h');
        sheet.style.removeProperty('--sheet-vv-top');
        return;
      }
      const h = Math.max(0, Math.round(vv.height));
      const top = Math.max(0, Math.round(vv.offsetTop || 0));
      /* the tallest viewport seen while open stands for "no keyboard", which also covers browsers that shrink
         the layout viewport together with the visual one */
      this._vvMax = Math.max(this._vvMax || 0, h);
      const full = Math.max(window.innerHeight || 0, this._vvMax);
      const zoomed = (vv.scale || 1) > 1.05;
      const kb = !zoomed && document.activeElement === this.input && full - h > 120;
      sheet.style.setProperty('--sheet-vv-h', h + 'px');
      sheet.style.setProperty('--sheet-vv-top', top + 'px');
      sheet.classList.add('is-vv');
      sheet.classList.toggle('is-kb', kb);
    }

    /* ---- tabs ------------------------------------------------------------------------------------------- */
    selectTab(name) {
      if (!TABS[name] || name === this.tab) return;
      this.tab = name;
      this.tabs.forEach((b) => {
        const on = b.dataset.searchTab === name;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      const cfg = TABS[name];
      if (this.typeField) this.typeField.value = cfg.searchType || '';
      if (this.submit) {
        const label = this.submit.dataset['label' + name.charAt(0).toUpperCase() + name.slice(1)];
        if (label) this.submit.textContent = label;
      }
      const q = this.input.value.trim();
      if (q) { this.search(q); } else { this.showDefaults(); }
    }

    defaultPanels() { return Array.from(this.querySelectorAll('[data-search-default-for]')); }
    get scrollBox() { return this.panel ? this.panel.querySelector('.search-panel__scroll') : null; }

    /* Hold the panel at the height it already has while the next set of rows is fetched, then let it settle.
       Without this the sheet collapsed to nothing between two tabs and snapped back open. */
    _hold() {
      const box = this.scrollBox;
      if (!box) return;
      const h = box.getBoundingClientRect().height;
      if (h > 24) box.style.minHeight = Math.round(h) + 'px';
    }
    _release() {
      const box = this.scrollBox;
      if (!box || !box.style.minHeight) return;
      /* a timer, not requestAnimationFrame: a background tab never paints, and the panel must not be left
         pinned to an old height when the shopper comes back to it */
      setTimeout(() => {
        box.style.minHeight = '0px';
        clearTimeout(this._relTimer);
        this._relTimer = setTimeout(() => { box.style.minHeight = ''; }, 260);
      }, 0);
    }
    /* one short entrance on whatever has just been swapped in */
    _fresh(el) {
      if (!el || theme.reducedMotion) return;
      el.classList.remove('is-fresh');
      void el.offsetWidth;
      el.classList.add('is-fresh');
    }

    /* ---- recent searches ------------------------------------------------------------------------------ */
    renderRecent() {
      const host = this.recentHost;
      if (!host) return;
      const list = this.recentOn ? Recent.list() : [];
      host.hidden = !list.length;
      if (!list.length) { host.replaceChildren(); return; }
      const d = host.dataset;
      const head = this._uid + '-recent-head';
      const removeLabel = (q) => (d.remove || '').split('[term]').join(q);
      host.innerHTML =
        '<div class="search-panel__row"><p class="search-panel__head" id="' + head + '">' + esc(d.heading || '') + '</p>' +
        (d.clear ? '<button type="button" class="search-panel__clear" data-search-clear' + (d.clearLabel ? ' aria-label="' + esc(d.clearLabel) + '"' : '') + '>' + esc(d.clear) + '</button>' : '') +
        '</div>' +
        '<ul class="search-recent__list" role="list" aria-labelledby="' + head + '">' +
        list.map((q) =>
          '<li class="search-recent__item">' +
            '<a class="sresult sresult--recent" href="' + esc(this.searchUrl + '?q=' + encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last' + this._hideOpt()) + '" data-term="' + esc(q) + '">' +
              CLOCK + '<span class="sresult__text"><span class="sresult__name">' + esc(q) + '</span></span>' +
            '</a>' +
            '<button type="button" class="search-recent__remove" data-recent-remove="' + esc(q) + '"' + (d.remove ? ' aria-label="' + esc(removeLabel(q)) + '"' : '') + '>' + CROSS + '</button>' +
          '</li>').join('') +
        '</ul>';
    }

    /* the empty sheet (no recents, no brands, nothing curated) shows no panel at all rather than a blank strip */
    updateEmpty() {
      if (!this.panel || !this.sheet) return;
      const live = !this.host.hidden;
      const shown = this.defaultPanels().filter((el) => !el.hidden);
      const has = shown.some((p) => Array.from(p.children).some((c) => c.tagName !== 'LINK' && c.tagName !== 'SCRIPT' && !c.hidden));
      this.panel.classList.toggle('is-empty', !live && !has);
    }

    onClick(e) {
      const rm = e.target.closest('[data-recent-remove]');
      if (rm && this.contains(rm)) {
        e.preventDefault();
        const before = Array.from(this.querySelectorAll('[data-recent-remove]'));
        const idx = before.indexOf(rm);
        const hadFocus = document.activeElement === rm;
        Recent.remove(rm.dataset.recentRemove);
        this.renderRecent();
        this.updateEmpty();
        const left = Array.from(this.querySelectorAll('[data-recent-remove]'));
        if (hadFocus || !this.contains(document.activeElement)) {
          focusEl(left[Math.min(idx, left.length - 1)] || this.input);
        }
        return;
      }
      if (e.target.closest('[data-search-clear]')) {
        e.preventDefault();
        Recent.clear();
        this.renderRecent();
        this.updateEmpty();
        if (!this.contains(document.activeElement)) this.focusInput();
        return;
      }
      const row = e.target.closest('.sresult, .search-panel__all, .brand-chip');
      if (row && this.contains(row) && !row.closest('[data-search-browse]')) this.remember(row.dataset.term || this.input.value);
    }

    onSubmit(e) {
      const q = this.input.value.trim();
      const cfg = TABS[this.tab] || TABS.product;
      if (!cfg.searchType) {
        /* Shopify has no collection search results page. Send the shopper to the collection index rather than
           to a product search that would silently ignore the tab. */
        e.preventDefault();
        if (q) this.remember(q);
        window.location.href = (this.form && this.form.dataset.collectionsUrl) || '/collections';
        return;
      }
      if (!q) { e.preventDefault(); return; }
      this.remember(q);
      const latin = latinFor(q);
      if (latin) this.input.value = latin;
    }

    showDefaults() {
      /* retire anything in flight: switching to an empty query used to leave a request running whose reply
         would then render under whichever tab happened to be selected when it arrived */
      if (this._ctrl) { this._ctrl.abort(); this._ctrl = null; }
      clearTimeout(this._skelTimer);
      this.seq++;
      this.classList.remove('is-loading');
      const panels = this.defaultPanels();
      if (panels.length) {
        let shown = false;
        panels.forEach((el) => {
          const on = el.dataset.searchDefaultFor === this.tab;
          el.hidden = !on;
          if (on) shown = true;
        });
        /* a tab with nothing curated shows the products panel (recent searches + brands) rather than a blank sheet */
        if (!shown && this.defaults) this.defaults.hidden = false;
      } else if (this.defaults) {
        this.defaults.hidden = false;
      }
      this.host.hidden = true;
      this.host.replaceChildren();
      this.updateEmpty();
      this._fresh(this.defaultPanels().find((el) => !el.hidden) || this.defaults);
      this._release();
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', this.host.id);
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
    }

    onInput() {
      clearTimeout(this._timer);
      const q = this.input.value.trim();
      this.classList.toggle('is-typing', !!q);
      if (!q) { if (this._ctrl) this._ctrl.abort(); this.showDefaults(); return; }
      this._timer = setTimeout(() => this.search(q), 200);
    }

    _showHost() {
      this.host.hidden = false;
      this.defaultPanels().forEach((el) => { el.hidden = true; });
      if (this.defaults) this.defaults.hidden = true;
      if (this.panel) this.panel.classList.remove('is-empty');
    }

    /* host ids come from the section as short local names; scope them so two instances never collide */
    _scopeIds(root) {
      const prefix = this.host.id + '-';
      const map = {};
      root.querySelectorAll('[id]').forEach((el) => {
        const next = prefix + el.id;
        map[el.id] = next;
        el.id = next;
      });
      root.querySelectorAll('[aria-labelledby]').forEach((el) => {
        el.setAttribute('aria-labelledby', el.getAttribute('aria-labelledby').split(/\s+/).map((x) => map[x] || x).join(' '));
      });
    }

    /* Honest no-results: the predictive section prints the message; the in-stock brand chips and the Browse row
       are copied in here so the request stays a single section render. The copied <sf-search-brands> fills itself
       from the session's cached answer (or stays hidden when no brand is proven in stock); _scopeIds then makes
       the copied heading ids unique. */
    _addBrands(root) {
      const empty = root.querySelector('[data-predictive-empty]');
      if (!empty) return;
      let at = empty;
      [['[data-search-brands]', 'search-brands--inline'], ['[data-search-browse]', 'search-browse--inline']].forEach(([sel, cls]) => {
        const src = this.querySelector(sel) || document.querySelector(sel);
        if (!src) return;
        const copy = src.cloneNode(true);
        copy.classList.add(cls);
        at.after(copy);
        at = copy;
      });
    }

    /* The products tab asked for more rows than it shows (see search()). Keep the first [limit] products and
       suggested searches, and keep the counts the announcement reads (data-products / data-suggestions) true. */
    _trim(root) {
      const res = root.matches('[data-predictive-results]') ? root : root.querySelector('[data-predictive-results]');
      if (!res) return;
      const cut = (sel) => {
        const rows = res.querySelectorAll(sel);
        for (let i = this.limit; i < rows.length; i++) rows[i].remove();
        return { before: rows.length, after: Math.min(rows.length, this.limit) };
      };
      const p = cut('.search-panel__group--products .sresult--product');
      const s = cut('.search-panel__group--queries .sresult');
      if (p.before !== p.after) res.dataset.products = String(p.after);
      if (s.before !== s.after) res.dataset.suggestions = String(Math.max(0, (parseInt(res.dataset.suggestions, 10) || 0) - (s.before - s.after)));
    }

    _announce(tab, q) {
      if (!this.status) return;
      const res = this.host.querySelector('[data-predictive-results]');
      const d = this.dataset;
      const np = res ? parseInt(res.dataset.products, 10) || 0 : 0;
      const ns = res ? parseInt(res.dataset.suggestions, 10) || 0 : 0;
      let msg;
      if (!np && !ns) {
        msg = d.strNone ? d.strNone + ' “' + q + '”' : (d.strNoResults || '');
      } else if (tab === 'product') {
        const parts = [];
        if (np === 1 && d.strProductOne) parts.push(d.strProductOne);
        else if (np) parts.push((d.strProducts || '[count]').replace('[count]', String(np)));
        else if (d.strNoProducts) parts.push(d.strNoProducts);
        if (ns === 1 && d.strSuggestionOne) parts.push(d.strSuggestionOne);
        else if (ns && d.strSuggestions) parts.push(d.strSuggestions.replace('[count]', String(ns)));
        msg = parts.join('. ');
      } else {
        msg = ns === 1 && d.strResultOne ? d.strResultOne : (d.strResults || '[count]').replace('[count]', String(ns));
      }
      this.status.textContent = msg;
    }

    async search(q) {
      if (this._ctrl) this._ctrl.abort();
      const ctrl = (this._ctrl = new AbortController());
      const mine = ++this.seq;          /* a late response from an earlier keystroke or tab must not win */
      const tab = this.tab;
      this.classList.add('is-loading');
      this._hold();
      /* a placeholder only after a short wait, so a fast reply never flashes one */
      clearTimeout(this._skelTimer);
      this._skelTimer = setTimeout(() => {
        if (mine !== this.seq || ctrl.signal.aborted) return;
        this.host.innerHTML = '<div class="search-panel__skeleton" aria-hidden="true"><span></span><span></span><span></span><span></span></div>';
        this._showHost();
      }, 140);
      if (this.status) this.status.textContent = this.dataset.strSearching || '';
      try {
        const u = new URL(this.url, location.origin);
        let types = (TABS[tab] || TABS.product).types;
        if (tab === 'product' && this.dataset.queries === 'false') types = 'product';
        /* the section drops products that fail the relevance gate (snippets/search-relevance.liquid), so the
           products tab asks for a few more than it shows (the API's maximum is 10) and _trim keeps [limit] */
        const ask = tab === 'product' ? Math.min(10, this.limit + 4) : this.limit;
        u.searchParams.set('q', latinFor(q) || q);
        u.searchParams.set('resources[type]', types);
        u.searchParams.set('resources[limit]', String(ask));
        u.searchParams.set('resources[limit_scope]', 'each');
        /* 'hide' when sold-out products are hidden from listings (data-unavailable, Theme settings › Filters) */
        u.searchParams.set('resources[options][unavailable_products]', this.dataset.unavailable === 'hide' ? 'hide' : 'last');
        u.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
        u.searchParams.set('section_id', 'predictive-search');
        const html = await theme.fetchText(u.toString(), { signal: ctrl.signal, headers: { Accept: 'text/html' } });
        if (ctrl.signal.aborted || mine !== this.seq) return;
        clearTimeout(this._skelTimer);
        const content = theme.sectionHtml(html, 'predictive-search', { inner: true });
        this.host.replaceChildren();
        if (content) {
          if (tab === 'product') this._trim(content);
          this._addBrands(content);
          this._scopeIds(content);
          this.host.appendChild(content);
        }
        this._showHost();
        this._fresh(this.host.firstElementChild);
        this._release();
        this.retargetSeeAll(q);
        const listbox = this.host.querySelector('[role="listbox"]');
        this.input.setAttribute('aria-controls', listbox ? listbox.id : this.host.id);
        this.input.setAttribute('aria-expanded', listbox ? 'true' : 'false');
        this.input.removeAttribute('aria-activedescendant');
        this.active = -1;
        this._announce(tab, q);
      } catch (err) {
        clearTimeout(this._skelTimer);
        if (err && err.name === 'AbortError' || mine !== this.seq) return;
        this._release();
        this.host.innerHTML = '<p class="search-panel__empty" role="alert">' + esc(theme.strings && theme.strings.error_generic || 'Something went wrong') + '</p>';
        this._showHost();
      } finally {
        if (!ctrl.signal.aborted && mine === this.seq) this.classList.remove('is-loading');
      }
    }

    /* The section always renders the products "see all" row; point it at whatever the active tab can really do. */
    /* the results page's own option for hidden sold-out products, so a link lands on the same list it counts */
    _hideOpt() {
      return this.dataset.unavailable === 'hide' ? '&options%5Bunavailable_products%5D=hide' : '';
    }

    retargetSeeAll(q) {
      const all = this.host.querySelector('[data-search-all]');
      if (!all) return;
      const cfg = TABS[this.tab] || TABS.product;
      if (!cfg.searchType) {
        all.setAttribute('href', (this.form && this.form.dataset.collectionsUrl) || '/collections');
        const label = this.submit && this.submit.dataset.labelCollection;
        const span = all.querySelector('span');
        if (span && label) span.textContent = label;
        return;
      }
      all.setAttribute('href', this.searchUrl + '?q=' + encodeURIComponent(q) + '&type=' + cfg.searchType + '&options%5Bprefix%5D=last' + this._hideOpt());
    }

    /* ---- keyboard ------------------------------------------------------------------------------------- */
    /* live results: options inside the listbox, walked with aria-activedescendant while focus stays in the field */
    options() { return this.host.hidden ? [] : Array.from(this.host.querySelectorAll('[role="option"]')).filter(visible); }
    /* before typing: recent terms, brand chips and Browse links are plain links, walked with real focus */
    defaultRows() {
      const box = this.defaultPanels().find((el) => !el.hidden) || this.defaults;
      return box ? Array.from(box.querySelectorAll('a.sresult, a.brand-chip')).filter(visible) : [];
    }
    highlight(dir) {
      const r = this.options();
      if (!r.length) return;
      if (this.active > -1 && r[this.active]) { r[this.active].classList.remove('is-active'); r[this.active].setAttribute('aria-selected', 'false'); }
      let next = this.active < 0 ? (dir > 0 ? 0 : r.length - 1) : this.active + dir;
      next = ((next % r.length) + r.length) % r.length;
      this.active = next;
      const el = r[next];
      el.classList.add('is-active');
      el.setAttribute('aria-selected', 'true');
      if (el.id) this.input.setAttribute('aria-activedescendant', el.id);
      el.scrollIntoView({ block: 'nearest' });
    }
    onKey(e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const dir = e.key === 'ArrowDown' ? 1 : -1;
        if (this.options().length) { e.preventDefault(); this.highlight(dir); return; }
        const rows = this.defaultRows();
        if (rows.length) {
          e.preventDefault();
          const el = dir > 0 ? rows[0] : rows[rows.length - 1];
          focusEl(el);
          el.scrollIntoView({ block: 'nearest' });
        }
        return;
      }
      if (e.key === 'Escape') {
        if (this.input.value) {
          e.preventDefault();
          e.stopPropagation();
          this.input.value = '';
          this.classList.remove('is-typing');
          this.showDefaults();
        }
        return;
      }
      if (e.key === 'Enter') {
        const r = this.options();
        if (this.active > -1 && r[this.active]) {
          e.preventDefault();
          this.remember(r[this.active].dataset.term || this.input.value);
          window.location.href = r[this.active].getAttribute('href');
        }
      }
    }
    onPanelKey(e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const rows = this.defaultRows();
      const i = rows.indexOf(document.activeElement);
      if (i < 0) return;
      e.preventDefault();
      if (e.key === 'ArrowUp' && i === 0) { this.focusInput(); return; }
      const el = rows[Math.max(0, Math.min(rows.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)))];
      focusEl(el);
      el.scrollIntoView({ block: 'nearest' });
    }
  }
  if (!customElements.get('sf-predictive-search')) customElements.define('sf-predictive-search', SFPredictiveSearch);
})();
